import { useState } from 'react';

import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';

import { View, ViewContext } from '../../enums';

import { getViewContext } from '../../utils';
import { getCurrentlyLoggedInEmployeeEntityId } from 'utils/misc';

import { Box, Button, Menu, MenuItem } from '@mui/material';

import usePlanningEntity from '../../hooks/usePlanningEntity';
import AddEditAppointmentDrawer from '../AddEditAppointmentDrawer/AddEditAppointmentDrawer';
import CopyPeriodDrawer from '../CopyPeriodDrawer';
import StructuralRoutesDialog from '../StructuralRoutesDialog';
import WorkPlacesDialog from '../WorkPlacesDialog';
import CollectedReportDrawer from '../../../../admin-portal/Procedures/Reports/CollectedReports/CollectedReportDrawer';
import { BASE_REPORTS } from '../../../../admin-portal/Procedures/Reports/type';
import { Icon } from '@sdb/planning-ui';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { ActionTooltip } from 'components/shared';

enum Actions {
  ManageRoutes = 'manageRoutes',
  ManageWorkPlaces = 'manageWorkPlaces',
  ManageProjects = 'manageProjects',
  AddAppointment = 'addAppointment',
  AddVVAppointment = 'addVVAppointment',
  AddProjectAppointment = 'addProjectAppointment',
  AddWPAppointment = 'addWPAppointment',
  SyncDuties = 'syncDuties',
  ConnectPlanning = 'connectPlanning',
  CopyPeriod = 'copyPeriod',
  MakeReport = 'makeProjectPlanningReport'
}

type Props = {
  view: View;
  handleSyncClick: () => void;
  handleConnectPlanningClick: (
    editAppointmentType: EditAppointmentType
  ) => void;
  disabled?: boolean;
};

export default function ActionButton({
  view,
  handleSyncClick,
  handleConnectPlanningClick,
  disabled
}: Props) {
  const { t } = useTranslation('common', { keyPrefix: 'activityPlanning' });
  const {
    hasActivityPlanningEditAccess,
    hasActivityPlanningBulkConnectSchedulesAccess,
    hasWorkPlacePlanningEditAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasProjectPlanningTeamViewAccess,
    hasManageAllProjectAccess,
    hasManageOwnProjectAccess,
    hasManageRoutes
  } = useSelector(({ grants }) => grants);

  const [action, setAction] = useState<Actions | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType = planningEntityTypePreference[view];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action: Actions) => () => {
    if (action == Actions.SyncDuties) {
      handleSyncClick();
    } else {
      setAction(action);
    }
    handleClose();
  };

  const handleDialogClose = () => {
    setAction(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'action-button' : undefined;

  const actions: Actions[] = [];
  const numberOfActiveModules = [
    hasActivityPlanningEditAccess,
    hasWorkPlacePlanningEditAccess,
    (hasMyActivitiesProjectAccess && getViewContext() === ViewContext.Own) ||
      hasManageOwnProjectsAccess
  ].filter(Boolean).length;
  if (numberOfActiveModules > 1) {
    if (hasActivityPlanningEditAccess) {
      actions.push(Actions.AddVVAppointment);
    }
    if (hasWorkPlacePlanningEditAccess) {
      actions.push(Actions.AddWPAppointment);
    }
    if (
      (hasMyActivitiesProjectAccess && getViewContext() === ViewContext.Own) ||
      hasManageOwnProjectsAccess
    ) {
      actions.push(Actions.AddProjectAppointment);
    }
  } else if (
    hasActivityPlanningEditAccess ||
    hasWorkPlacePlanningEditAccess ||
    (hasMyActivitiesProjectAccess && getViewContext() === ViewContext.Own) ||
    hasManageOwnProjectsAccess
  ) {
    actions.push(Actions.AddAppointment);
  }
  if (
    view === View.Roster &&
    ((planningEntityType === 'WORKPLACE' && hasWorkPlacePlanningEditAccess) ||
      (planningEntityType === 'PROJECT' &&
        hasMyActivitiesProjectAccess &&
        getViewContext() === ViewContext.Own))
  ) {
    actions.push(Actions.CopyPeriod);
  }
  if (hasWorkPlacePlanningEditAccess) {
    actions.push(Actions.ManageWorkPlaces);
  }

  if (hasActivityPlanningEditAccess) {
    actions.push(Actions.SyncDuties);
  }

  if (
    hasActivityPlanningBulkConnectSchedulesAccess &&
    planningEntityType === 'EMPLOYEE'
  ) {
    actions.push(Actions.ConnectPlanning);
  }

  if (hasManageRoutes) {
    actions.push(Actions.ManageRoutes);
  }

  if (
    (hasProjectPlanningTeamViewAccess ||
      hasManageAllProjectAccess ||
      hasManageOwnProjectAccess) &&
    getViewContext() === ViewContext.Team
  ) {
    actions.push(Actions.MakeReport);
  }

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        startIcon={<Icon type={'layer-group' as const} fontSize="inherit" />}
        endIcon={<Icon type={'chevron-down' as const} fontSize="inherit" />}
        onClick={handleClick}
        disabled={disabled}
      />
      <Menu
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        {actions.map((action) => (
          <MenuItem key={action} onClick={handleActionClick(action)}>
            {t(action)}
          </MenuItem>
        ))}
      </Menu>
      {actions.includes(Actions.ManageRoutes) && (
        <StructuralRoutesDialog
          open={action === Actions.ManageRoutes}
          handleClose={handleDialogClose}
        />
      )}
      {actions.includes(Actions.ManageWorkPlaces) && (
        <WorkPlacesDialog
          open={action === Actions.ManageWorkPlaces}
          handleClose={handleDialogClose}
        />
      )}
      {actions.includes(Actions.MakeReport) && (
        <CollectedReportDrawer
          forcedDefaultReportType={BASE_REPORTS.PROJECTPLANNING_REPORT}
          containerOpen={action === Actions.MakeReport}
          handleClose={handleDialogClose}
        />
      )}
      {[
        Actions.AddAppointment,
        Actions.AddVVAppointment,
        Actions.AddWPAppointment
      ].some((a) => actions.includes(a)) && (
        <AddEditAppointmentDrawer
          open={
            action === Actions.AddAppointment ||
            action === Actions.AddWPAppointment ||
            action === Actions.AddVVAppointment ||
            action === Actions.AddProjectAppointment
          }
          schedulingLabel={(() => {
            switch (action) {
              case Actions.AddWPAppointment:
                return 'WORKPLACE';
              case Actions.AddVVAppointment:
                return 'VV';
              case Actions.AddProjectAppointment:
                return 'PROJECT';
              default:
                if (hasWorkPlacePlanningEditAccess) return 'WORKPLACE';
                if (
                  (hasMyActivitiesProjectAccess &&
                    getViewContext() === ViewContext.Own) ||
                  hasManageOwnProjectsAccess
                )
                  return 'PROJECT';
                return 'VV';
            }
          })()}
          handleClose={handleDialogClose}
          isEmployeePerspective={planningEntityType === 'EMPLOYEE'}
        />
      )}
      {actions.includes(Actions.CopyPeriod) && (
        <CopyPeriodDrawer
          open={action === Actions.CopyPeriod}
          employeeEntityId={
            getViewContext() === ViewContext.Own
              ? getCurrentlyLoggedInEmployeeEntityId()
              : undefined
          }
          handleClose={handleDialogClose}
        />
      )}
      {actions.includes(Actions.ConnectPlanning) && (
        <ActionTooltip
          title={t('editRecurringAppointments')}
          open={action === Actions.ConnectPlanning}
          handleClose={handleDialogClose}
          actionButtons={[
            {
              label: t('onlyTheseAppointments'),
              variant: 'outlined',
              onClick: () => {
                handleDialogClose();
                handleConnectPlanningClick('ONLY_THIS');
              }
            },
            {
              label: t('theseAndSubsequentAppointments'),
              variant: 'outlined',
              onClick: () => {
                handleDialogClose();
                handleConnectPlanningClick('THIS_AND_ALL_SUBSEQUENT');
              }
            }
          ]}
        >
          <Box />
        </ActionTooltip>
      )}
    </div>
  );
}
